<script setup>
import { ref } from 'vue';
import { VueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

// Vehicle 1: Global ETFs & US Stock UDRs
const vehicle1Nodes = ref([
  {
    id: 'v1-step1',
    type: 'default',
    position: { x: 20, y: 20 },
    data: { label: '📝 STEP 1: Open IFSC Trading & Demat' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)',
      whiteSpace: 'pre-wrap'
    }
  },
  {
    id: 'v1-step1-detail',
    type: 'default',
    position: { x: 240, y: 20 },
    data: { label: 'Platform: HDFC Securities IFSC | KYC: Digital (PAN, Aadhaar, Video) | Approval: 48-72 hrs' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      border: '2px solid #3b82f6',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#1e3a8a',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(59, 130, 246, 0.15)'
    }
  },
  {
    id: 'v1-step2',
    type: 'default',
    position: { x: 20, y: 100 },
    data: { label: '💰 STEP 2: Fund IBU Account' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.3)'
    }
  },
  {
    id: 'v1-step2-detail',
    type: 'default',
    position: { x: 240, y: 100 },
    data: { label: 'Transfer USD to GIFT City IBU FCA | Purpose Code: S0001 | TCS: 20% on >₹10L/year' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(16, 185, 129, 0.15)'
    }
  },
  {
    id: 'v1-step3',
    type: 'default',
    position: { x: 20, y: 180 },
    data: { label: '📊 STEP 3: Execute Trade' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(245, 158, 11, 0.3)'
    }
  },
  {
    id: 'v1-step3-detail',
    type: 'default',
    position: { x: 240, y: 180 },
    data: { label: 'UDR: AAPL, MSFT | ETF: VOO, QQQ | Timing: 🕗 7:30 PM – 2:00 AM IST (🇺🇸 market hours)' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '2px solid #f59e0b',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#92400e',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(245, 158, 11, 0.15)'
    }
  }
]);

const vehicle1Edges = ref([
  { id: 'v1-e1', source: 'v1-step1', target: 'v1-step1-detail', animated: true, style: { stroke: '#3b82f6', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#3b82f6' } },
  { id: 'v1-e2', source: 'v1-step1-detail', target: 'v1-step2', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#10b981' } },
  { id: 'v1-e3', source: 'v1-step2', target: 'v1-step2-detail', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#10b981' } },
  { id: 'v1-e4', source: 'v1-step2-detail', target: 'v1-step3', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#f59e0b' } },
  { id: 'v1-e5', source: 'v1-step3', target: 'v1-step3-detail', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#f59e0b' } }
]);

// Vehicle 2: GIFT City Active Funds (USD)
const vehicle2Nodes = ref([
  {
    id: 'v2-step1',
    type: 'default',
    position: { x: 520, y: 20 },
    data: { label: '📝 STEP 1: AMC Onboarding' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(139, 92, 246, 0.3)'
    }
  },
  {
    id: 'v2-step1-detail',
    type: 'default',
    position: { x: 740, y: 20 },
    data: { label: 'DSP/Edelweiss/Sundaram GIFT Portal | Subscribe: DSP World Gold or Global Innovation Fund' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
      border: '2px solid #8b5cf6',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#581c87',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(139, 92, 246, 0.15)'
    }
  },
  {
    id: 'v2-step2',
    type: 'default',
    position: { x: 520, y: 100 },
    data: { label: '🔗 STEP 2: Link IBU Account' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(6, 182, 212, 0.3)'
    }
  },
  {
    id: 'v2-step2-detail',
    type: 'default',
    position: { x: 740, y: 100 },
    data: { label: 'Provide IFSC IBU account details (HDFC/ICICI) | Must flow from IBU, not INR account' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)',
      border: '2px solid #06b6d4',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#164e63',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(6, 182, 212, 0.15)'
    }
  },
  {
    id: 'v2-step3',
    type: 'default',
    position: { x: 520, y: 180 },
    data: { label: '💼 STEP 3: Subscribe & Allotment' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '10px 14px',
      borderRadius: '8px',
      width: '200px',
      textAlign: 'center',
      boxShadow: '0 3px 12px rgba(236, 72, 153, 0.3)'
    }
  },
  {
    id: 'v2-step3-detail',
    type: 'default',
    position: { x: 740, y: 180 },
    data: { label: 'Transfer USD to AMC pool account | Allotment: Next Day NAV (USD) | Units in SoA or IFSC Demat' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
      border: '2px solid #ec4899',
      borderRadius: '8px',
      padding: '10px',
      width: '240px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '9.5px',
      color: '#831843',
      fontWeight: '500',
      boxShadow: '0 2px 10px rgba(236, 72, 153, 0.15)'
    }
  }
]);

const vehicle2Edges = ref([
  { id: 'v2-e1', source: 'v2-step1', target: 'v2-step1-detail', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#8b5cf6' } },
  { id: 'v2-e2', source: 'v2-step1-detail', target: 'v2-step2', animated: true, style: { stroke: '#06b6d4', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#06b6d4' } },
  { id: 'v2-e3', source: 'v2-step2', target: 'v2-step2-detail', animated: true, style: { stroke: '#06b6d4', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#06b6d4' } },
  { id: 'v2-e4', source: 'v2-step2-detail', target: 'v2-step3', animated: true, style: { stroke: '#ec4899', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#ec4899' } },
  { id: 'v2-e5', source: 'v2-step3', target: 'v2-step3-detail', animated: true, style: { stroke: '#ec4899', strokeWidth: 2 }, type: 'smoothstep', markerEnd: { type: 'arrowclosed', color: '#ec4899' } }
]);
</script>

<template>
  <div class="gift-etf-udr-workflow">
    <h2 class="workflow-title">GIFT City ETF & UDR Investment Vehicles</h2>
    <p class="workflow-intro">
      <strong>Two Investment Routes:</strong> Choose between passive ETF/UDR tracking or active USD fund management in GIFT City.
    </p>

    <!-- Two Workflows Stacked Vertically -->
    <div class="workflows-stack">
      <!-- Vehicle 1: Global ETFs & US Stock UDRs -->
      <div class="workflow-section">
        <h3 class="section-title">🌍 Vehicle 1: Global ETFs & US Stock UDRs</h3>
        <p class="section-subtitle">Best for: Passive index tracking (Vanguard S&P 500) or top 50 US blue-chips</p>

        <!-- Nuance Boxes -->
        <div class="nuance-boxes">
          <div class="nuance-box udr">
            <div class="nuance-header">
              <span class="nuance-icon">📄</span>
              <strong>US Stock UDRs (NSE IX Receipts)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are "Local" versions of US stocks.</p>
            <p class="nuance-detail"><strong>Process:</strong> Buy them on NSE IX exchange using your IFSC Broker (HDFC Securities IFSC).</p>
            <p class="nuance-detail"><strong>Custody:</strong> Held in your CDSL-IFSC Demat. Highest protection route under Indian IFSC law.</p>
          </div>
          <div class="nuance-box etf">
            <div class="nuance-header">
              <span class="nuance-icon">📊</span>
              <strong>Global ETFs (Passive USD Tracking)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are "True" Global ETFs (like Vanguard S&P 500 - VOO) available on GIFT City exchanges.</p>
            <p class="nuance-detail"><strong>Process:</strong> Similar to UDRs, buy these on the exchange via your IFSC Broker.</p>
            <p class="nuance-detail"><strong>Difference from UDRs:</strong> While UDRs are "receipts" for individual stocks (like Apple), these are units of a massive USD-denominated basket.</p>
          </div>
        </div>

        <div class="workflow-container" style="height: 300px; border: 2px solid #e5e7eb; border-radius: 12px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);">
          <VueFlow
            :nodes="vehicle1Nodes"
            :edges="vehicle1Edges"
            :fit-view-on-init="false"
            :min-zoom="0.5"
            :max-zoom="1.5"
            :default-viewport="{ zoom: 1, x: 0, y: 0 }"
          >
            <Background pattern-color="#d1d5db" :gap="16" size="0.8" />
            <Controls />
          </VueFlow>
        </div>
      </div>

      <!-- Vehicle 2: GIFT City Active Funds -->
      <div class="workflow-section">
        <h3 class="section-title">💼 Vehicle 2: GIFT City Active Funds (USD)</h3>
        <p class="section-subtitle">Best for: Active alpha generation by Indian fund managers in tax-neutral USD zone</p>

        <!-- Nuance Box -->
        <div class="nuance-boxes">
          <div class="nuance-box active-funds">
            <div class="nuance-header">
              <span class="nuance-icon">💼</span>
              <strong>GIFT City Active Funds (USD Mutual Funds)</strong>
            </div>
            <p class="nuance-detail"><strong>The Nuance:</strong> These are USD-denominated mutual funds managed by Indian AMCs (like DSP or HDFC AMC) located in GIFT City.</p>
            <p class="nuance-detail"><strong>Process:</strong> You transfer USD from your IBU-FCA directly to the AMC's pool account in GIFT City.</p>
            <p class="nuance-detail"><strong>Best For:</strong> Investors who want professional management but want to stay within the tax-neutral GIFT zone.</p>
          </div>
        </div>

        <div class="workflow-container" style="height: 300px; border: 2px solid #e5e7eb; border-radius: 12px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);">
          <VueFlow
            :nodes="vehicle2Nodes"
            :edges="vehicle2Edges"
            :fit-view-on-init="false"
            :min-zoom="0.5"
            :max-zoom="1.5"
            :default-viewport="{ zoom: 1, x: 0, y: 0 }"
          >
            <Background pattern-color="#d1d5db" :gap="16" size="0.8" />
            <Controls />
          </VueFlow>
        </div>
      </div>
    </div>

    <!-- Simplified Funding: Payment Gateway Route -->
    <div class="gateway-section">
      <h3>⚡ Simplified Funding: The Payment Gateway Route (No Personal FCA/IBU)</h3>
      <p class="gateway-intro">
        Vehicle 2 (Active Funds) traditionally needs USD to flow from <strong>your own</strong> IBU-FCA. But a growing number of GIFT City &amp; global AMCs now offer a <strong>payment-gateway route</strong>: you pay in INR from your <strong>existing domestic savings account</strong>, and the gateway wires it via SWIFT into the <strong>fund's pooled USD Current Account</strong> at a domestic bank — no personal FCA/IBU required.
      </p>

      <div class="gateway-grid">
        <div class="gateway-step">
          <div class="gw-num">1</div>
          <strong>Pay from INR Savings A/c</strong>
          <p>Use the fund's gateway (e.g., Marcellus "Glomo"). No GIFT City account to open.</p>
        </div>
        <div class="gateway-step">
          <div class="gw-num">2</div>
          <strong>Auto Form A2 + TCS</strong>
          <p>Gateway generates the Digital Form A2 &amp; tracks the 20% TCS (&gt;₹10L/year) for you.</p>
        </div>
        <div class="gateway-step">
          <div class="gw-num">3</div>
          <strong>SWIFT → Fund's USD A/c</strong>
          <p>INR is converted and wired to the AMC's <strong>USD Current Account</strong> (e.g., at Kotak Mahindra Bank) via its SWIFT code.</p>
        </div>
        <div class="gateway-step">
          <div class="gw-num">4</div>
          <strong>Units Allotted</strong>
          <p>Units of the USD fund are credited inside the fund/app wrapper at the next NAV.</p>
        </div>
      </div>

      <div class="gateway-example">
        <span class="gw-example-icon">💡</span>
        <div>
          <strong>Real-world example:</strong> For the <em>Marcellus Global Equities Fund</em> — a concentrated portfolio of durable, hard-to-disrupt businesses across the 🇺🇸 US, Europe, Taiwan &amp; Canada, selected via deep research and a strict risk &amp; fair-value discipline — you can remit straight from your <strong>ICICI Bank savings account</strong> through Marcellus's <strong>"Glomo" gateway</strong> into its <strong>Kotak Mahindra Bank USD Current Account</strong> (via SWIFT). No FCA/IBU needed.
          <p class="gw-note">This is <strong>not limited to one fund</strong> — platforms &amp; AMCs such as <strong>Vested Finance, INDmoney, Stockal, DSP, Mirae, Edelweiss</strong> and several GIFT City IFSC funds offer similar "pay-from-your-savings-account" rails. <em>Trade-off:</em> the gateway/pooled route is fund-specific and convenient, whereas your own FCA/IBU lets you hold USD indefinitely and redeploy across many products.</p>
        </div>
      </div>
    </div>

    <!-- Key Comparison -->
    <div class="comparison-section">
      <h3>📊 Quick Comparison</h3>
      <div class="comparison-grid">
        <div class="comparison-card">
          <h4>🌍 ETFs & UDRs</h4>
          <ul>
            <li>✅ Low cost, passive tracking</li>
            <li>✅ 48-72 hour account setup</li>
            <li>✅ Trade during US market hours</li>
            <li>⚠️ Limited to popular stocks/indices</li>
          </ul>
        </div>
        <div class="comparison-card">
          <h4>💼 Active Funds</h4>
          <ul>
            <li>✅ Professional fund management</li>
            <li>✅ Tax-neutral USD zone</li>
            <li>✅ Next-day NAV allotment</li>
            <li>⚠️ Higher fees than passive ETFs</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Investment Limits & Regulatory Boundaries -->
    <div class="limits-section">
      <h3>💰 Investment Limits & Regulatory Boundaries (Important)</h3>

      <!-- Example GIFT City Funds -->
      <div class="limit-subsection">
        <h4>📌 Example GIFT City Funds (Reference for Investors)</h4>
        <div class="fund-examples">
          <div class="fund-category">
            <strong>Global Outbound (USD):</strong>
            <ul>
              <li>DSP Global Equity Fund (IFSC – Global Active Equity)</li>
              <li>Parag Parikh IFSC S&P 500 Fund of Fund (Passive)</li>
              <li>Parag Parikh IFSC Nasdaq 100 Fund of Fund (Passive)</li>
            </ul>
          </div>
          <div class="fund-category">
            <strong>India Inbound (USD-denominated):</strong>
            <ul>
              <li>Tata India Dynamic Equity Fund – GIFT IFSC</li>
              <li>Edelweiss Greater China Equity Fund – IFSC</li>
              <li>Sundaram India Mid Cap Fund – GIFT IFSC</li>
            </ul>
          </div>
        </div>
        <p class="subsection-note">→ These are USD-denominated mutual funds launched from GIFT City IFSC covering both global (outbound) and India (inbound) exposure.</p>
      </div>

      <!-- Minimum Investment -->
      <div class="limit-subsection">
        <h4>1️⃣ Minimum Investment (Lower Limit)</h4>
        <div class="investment-route">
          <strong>ETFs / UDRs (Exchange-based):</strong>
          <ul>
            <li>No formal "minimum investment" at product level</li>
            <li>Practically equal to price of 1 unit/share (e.g., $50–$500 typical range)</li>
            <li>Broker or platform may impose operational minimum (e.g., $1,000 account funding)</li>
          </ul>
        </div>
        <div class="investment-route">
          <strong>GIFT City Active Funds (USD Mutual Funds):</strong>
          <ul>
            <li>Typical AMC minimum: ~$5,000 initial investment (e.g., DSP Global Equity Fund, PPFAS IFSC Funds)</li>
            <li>Additional investments: ~$500</li>
            <li>Some India-focused funds (e.g., Tata India Dynamic Equity Fund, Edelweiss China Fund) may start as low as ~$500</li>
          </ul>
        </div>
        <p class="subsection-note">→ Higher minimums are driven by offshore USD structure, fund administration, and regulatory overhead—not by retail MF norms</p>
      </div>

      <!-- Maximum Investment -->
      <div class="limit-subsection">
        <h4>2️⃣ Maximum Investment (Upper Limit)</h4>
        <div class="lrs-box">
          <p><strong>All GIFT City investments fall under LRS (Liberalised Remittance Scheme):</strong></p>
          <ul>
            <li><strong>Current limit:</strong> USD 250,000 per individual per financial year</li>
            <li>Applies across all foreign investments combined (stocks, funds, property)</li>
            <li>Not specific to GIFT City funds</li>
          </ul>
        </div>
        <p class="subsection-note">→ No upper cap at fund level — only LRS acts as effective ceiling</p>
      </div>

      <!-- Ticket Size Positioning -->
      <div class="limit-subsection">
        <h4>3️⃣ Ticket Size Positioning (Investor Behaviour Insight)</h4>
        <div class="positioning-grid">
          <div class="positioning-card">
            <strong>ETFs / UDRs:</strong>
            <ul>
              <li>Flexible ticket sizing (small to large)</li>
              <li>Suitable for staggered investing / SIP-like behaviour</li>
            </ul>
          </div>
          <div class="positioning-card">
            <strong>Active Funds:</strong>
            <ul>
              <li>Designed for allocation decisions (lump sum blocks)</li>
              <li>Typical use: $5,000+ strategic allocation</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Currency & Funding -->
      <div class="limit-subsection">
        <h4>4️⃣ Currency & Funding Constraints</h4>
        <div class="funding-info">
          <p><strong>All investments are USD-denominated</strong></p>
          <p><strong>Funding happens via:</strong></p>
          <ul>
            <li>LRS remittance (INR → USD)</li>
            <li>OR IFSC Banking Unit (IBU) account</li>
          </ul>
        </div>
        <div class="highlight-notes">
          <p>→ INR cannot be directly invested</p>
          <p>→ Forex conversion cost applies</p>
        </div>
      </div>

      <!-- Liquidity / Exit -->
      <div class="limit-subsection">
        <h4>5️⃣ Liquidity / Exit Constraints</h4>
        <div class="positioning-grid">
          <div class="positioning-card">
            <strong>ETFs / UDRs:</strong>
            <ul>
              <li>Intraday liquidity (market dependent)</li>
              <li>Real-time pricing like stocks</li>
            </ul>
          </div>
          <div class="positioning-card">
            <strong>Active Funds:</strong>
            <ul>
              <li>NAV-based execution (T+1 or similar)</li>
              <li>No intraday exit</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Tax & Structuring -->
      <div class="limit-subsection">
        <h4>6️⃣ Tax & Structuring Implications (High-level)</h4>
        <ul class="tax-points">
          <li>Funds regulated by IFSCA (not SEBI)</li>
          <li>Different tax structure vs domestic mutual funds</li>
          <li>Investor-level taxation depends on residency</li>
        </ul>
      </div>

      <!-- Advisory Insight -->
      <div class="advisory-insight-box">
        <div class="insight-icon">👉</div>
        <div class="insight-content">
          <strong>Advisory Insight:</strong>
          <p>Investors typically use:</p>
          <ul>
            <li><strong>ETFs/UDRs</strong> for tactical, low-cost global exposure</li>
            <li><strong>Active GIFT funds</strong> (e.g., DSP, PPFAS) for strategic allocation blocks ($5K+)</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gift-etf-udr-workflow {
  max-width: 1400px;
  margin: 0 auto;
}

.workflow-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.workflow-intro {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.1);
}

.workflow-intro strong {
  color: #1e40af;
  font-weight: 700;
}

.workflows-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.workflow-section {
  background: white;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: 0.7rem;
  color: #6b7280;
  margin: 0 0 10px 0;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nuance-boxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.workflow-section:last-child .nuance-boxes {
  grid-template-columns: 1fr;
}

.nuance-box {
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border: 2px solid #fde047;
  border-radius: 8px;
  padding: 10px;
  font-size: 0.75rem;
}

.nuance-box.udr {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-color: #38bdf8;
}

.nuance-box.etf {
  background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%);
  border-color: #a78bfa;
}

.nuance-box.active-funds {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  border-color: #f9a8d4;
}

.nuance-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.nuance-icon {
  font-size: 1rem;
}

.nuance-header strong {
  color: #1f2937;
  font-size: 0.8rem;
}

.nuance-detail {
  margin: 4px 0;
  color: #374151;
  line-height: 1.4;
}

.nuance-detail strong {
  color: #1f2937;
  font-weight: 600;
}

/* Payment Gateway Route */
.gateway-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 10px;
  border: 2px solid #6ee7b7;
}

.gateway-section h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #065f46;
  margin: 0 0 8px 0;
}

.gateway-intro {
  font-size: 0.8rem;
  color: #047857;
  margin: 0 0 14px 0;
  line-height: 1.5;
}

.gateway-intro strong {
  color: #064e3b;
}

.gateway-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

.gateway-step {
  position: relative;
  background: white;
  border: 2px solid #a7f3d0;
  border-radius: 8px;
  padding: 12px 10px 10px;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.1);
}

.gateway-step .gw-num {
  position: absolute;
  top: -10px;
  left: 10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

.gateway-step strong {
  display: block;
  font-size: 0.78rem;
  color: #064e3b;
  margin: 6px 0 4px;
}

.gateway-step p {
  margin: 0;
  font-size: 0.7rem;
  color: #4b5563;
  line-height: 1.4;
}

.gateway-step p strong {
  display: inline;
  font-size: 0.7rem;
  color: #065f46;
}

.gateway-example {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
}

.gw-example-icon {
  font-size: 1.3rem;
  flex-shrink: 0;
}

.gateway-example > div {
  font-size: 0.78rem;
  color: #78350f;
  line-height: 1.55;
}

.gateway-example strong {
  color: #92400e;
}

.gateway-example em {
  font-style: italic;
  font-weight: 600;
  color: #92400e;
}

.gw-note {
  margin: 8px 0 0 0;
  font-size: 0.74rem;
  color: #92400e;
  line-height: 1.5;
}

.comparison-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 10px;
  border: 2px solid #bae6fd;
}

.comparison-section h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 12px 0;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.comparison-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid #bae6fd;
  transition: all 0.3s ease;
}

.comparison-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
}

.comparison-card h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.comparison-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comparison-card ul li {
  padding: 4px 0;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Investment Limits Section */
.limits-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 2px solid #fbbf24;
}

.limits-section h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #78350f;
  margin: 0 0 16px 0;
}

.limit-subsection {
  background: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #fde68a;
}

.limit-subsection:last-of-type {
  margin-bottom: 12px;
}

.limit-subsection h4 {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.fund-examples {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 8px;
}

.fund-category {
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #8b5cf6;
}

.fund-category strong {
  display: block;
  color: #7c3aed;
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.fund-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.fund-category ul li {
  padding: 3px 0;
  color: #4b5563;
  font-size: 0.7rem;
  line-height: 1.3;
}

.investment-route {
  margin-bottom: 10px;
  padding: 10px;
  background: #f9fafb;
  border-radius: 6px;
}

.investment-route strong {
  display: block;
  color: #1f2937;
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.investment-route ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.investment-route ul li {
  padding: 3px 0 3px 12px;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
  position: relative;
}

.investment-route ul li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

.subsection-note {
  margin: 8px 0 0 0;
  padding: 8px;
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #1e40af;
  line-height: 1.4;
}

.lrs-box {
  padding: 10px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #10b981;
  border-radius: 6px;
  margin-bottom: 8px;
}

.lrs-box p {
  margin: 0 0 8px 0;
  color: #065f46;
  font-size: 0.8rem;
}

.lrs-box ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.lrs-box ul li {
  padding: 3px 0 3px 12px;
  color: #064e3b;
  font-size: 0.75rem;
  line-height: 1.4;
  position: relative;
}

.lrs-box ul li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #10b981;
}

.positioning-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.positioning-card {
  padding: 10px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.positioning-card strong {
  display: block;
  color: #1f2937;
  font-size: 0.8rem;
  margin-bottom: 6px;
}

.positioning-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.positioning-card ul li {
  padding: 3px 0 3px 12px;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
  position: relative;
}

.positioning-card ul li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

.funding-info {
  padding: 10px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.funding-info p {
  margin: 0 0 6px 0;
  color: #1f2937;
  font-size: 0.8rem;
}

.funding-info ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.funding-info ul li {
  padding: 3px 0 3px 12px;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
  position: relative;
}

.funding-info ul li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

.highlight-notes {
  padding: 8px;
  background: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
}

.highlight-notes p {
  margin: 4px 0;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 600;
}

.tax-points {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tax-points li {
  padding: 6px 0 6px 12px;
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.4;
  position: relative;
}

.tax-points li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

.advisory-insight-box {
  display: flex;
  gap: 12px;
  padding: 14px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 2px solid #3b82f6;
  border-radius: 8px;
  margin-top: 12px;
}

.insight-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.insight-content {
  flex: 1;
}

.insight-content strong {
  display: block;
  color: #1e40af;
  font-size: 0.9rem;
  margin-bottom: 6px;
}

.insight-content p {
  margin: 0 0 6px 0;
  color: #1e3a8a;
  font-size: 0.8rem;
}

.insight-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.insight-content ul li {
  padding: 3px 0;
  color: #1e3a8a;
  font-size: 0.75rem;
  line-height: 1.4;
}

@media (max-width: 1200px) {
  .workflows-stack {
    gap: 24px;
  }

  .fund-examples,
  .positioning-grid {
    grid-template-columns: 1fr;
  }

  .gateway-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .workflow-title {
    font-size: 1.5rem;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .workflow-container {
    height: 350px !important;
  }

  .workflows-stack {
    gap: 20px;
  }

  .fund-examples,
  .positioning-grid {
    grid-template-columns: 1fr;
  }

  .gateway-grid {
    grid-template-columns: 1fr;
  }
}
</style>
